export type BeatLineSortableContext = {
  state: Record<string, any>;
  online?: Record<string, any>;
  t?: (key: string, fallback?: string) => string;
  alert?: (message: string) => void;
  beatlineSetScrollLock?: (locked: boolean) => void;
  beatlineCancelNudge?: () => void;
};

export type BeatLineSortableResult = { handled: boolean; reason?: string; sortable?: any };

declare const Sortable: any;

function safeCall(fn: unknown, ...args: any[]): void {
  if (typeof fn !== 'function') return;
  try { fn(...args); } catch (_) {}
}

function t(ctx: BeatLineSortableContext, key: string, fallback = key): string {
  return typeof ctx.t === 'function' ? ctx.t(key, fallback) : fallback;
}

export function setDragEnabledFromTypeScript(enabled: boolean): BeatLineSortableResult {
  try {
    const w = window as any;
    const sortables = w.beatlineSortables || {};
    Object.keys(sortables).forEach((key) => {
      const sortable = sortables[key];
      if (!sortable || typeof sortable.option !== 'function') return;
      if (key === 'deck-zone' || key.startsWith('timeline-p')) sortable.option('disabled', !enabled);
    });
    return { handled: true, reason: 'drag-state-updated' };
  } catch (_) {
    return { handled: false, reason: 'drag-state-failed' };
  }
}

export function initSortableFromTypeScript(id: string, group: string, allowDrop: boolean, ctx: BeatLineSortableContext): BeatLineSortableResult {
  const el = document.getElementById(id);
  if (!el) return { handled: false, reason: 'missing-element' };
  const w = window as any;
  const SortableCtor = w.Sortable || (typeof Sortable !== 'undefined' ? Sortable : null);
  if (!SortableCtor) return { handled: false, reason: 'missing-sortable' };

  w.beatlineSortables = w.beatlineSortables || {};
  if (w.beatlineSortables[id] && typeof w.beatlineSortables[id].destroy === 'function') {
    try { w.beatlineSortables[id].destroy(); } catch (_) {}
  }

  const sortable = new SortableCtor(el, {
    group: { name: group, pull: true, put: allowDrop },
    animation: 150,
    draggable: '.card',
    sort: true,
    scrollSensitivity: 70,
    scrollSpeed: 15,
    filter: '.revealed, .place-btn',
    preventOnFilter: true,
    forceFallback: true,
    fallbackOnBody: true,
    fallbackTolerance: 5,
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    fallbackClass: 'sortable-fallback',
    onStart: () => {
      document.body.classList.add('beatline-dragging');
      safeCall(ctx.beatlineSetScrollLock, true);
      safeCall(ctx.beatlineCancelNudge);
    },
    onEnd: (evt: any) => {
      document.body.classList.remove('beatline-dragging');
      const app = document.getElementById('beatline-app');
      const keepLocked = !!((app && app.classList.contains('fullscreen-active')) || document.fullscreenElement);
      if (!keepLocked) safeCall(ctx.beatlineSetScrollLock, false);
      document.querySelectorAll('.sortable-fallback, .sortable-drag').forEach((node) => {
        if ((node as HTMLElement).parentElement !== document.body) return;
        if (evt && evt.item && node === evt.item) return;
        node.remove();
      });
    },
    onAdd: (evt: any) => {
      if (!id.startsWith('timeline-p')) return;
      const target = parseInt(id.replace('timeline-p', ''), 10);
      const state = ctx.state || {};
      const online = ctx.online || {};
      if (online.enabled) {
        if (parseInt(String(online.player || 0), 10) !== target || parseInt(String(state.turn || 0), 10) !== parseInt(String(online.player || 0), 10)) {
          const msg = t(ctx, 'alert_not_your_timeline', 'Niet jouw tijdlijn');
          if (typeof ctx.alert === 'function') ctx.alert(msg); else window.alert(msg);
          if (evt.from && evt.item) evt.from.appendChild(evt.item);
        }
      } else if (parseInt(String(state.turn || 0), 10) !== target) {
        const msg = t(ctx, 'alert_not_your_timeline', 'Niet jouw tijdlijn');
        if (typeof ctx.alert === 'function') ctx.alert(msg); else window.alert(msg);
        if (evt.from && evt.item) evt.from.appendChild(evt.item);
      }
    },
  });

  w.beatlineSortables[id] = sortable;
  return { handled: true, reason: 'sortable-initialized', sortable };
}
